import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Card,
  Group,
  Stack,
  Badge,
  Avatar,
  ThemeIcon,
  SimpleGrid,
  Box,
  Collapse,
  ActionIcon,
  Loader,
  Center,
  ScrollArea,
  Divider,
  Paper,
  Breadcrumbs,
  Anchor,
  Modal,
  TextInput,
  Textarea,
  Select
} from '@mantine/core';
import { personasAPI, preferencesAPI, friendsAPI, personaPrivacyAPI } from '../services/api';

const FriendPersonasPage = () => {
  const navigate = useNavigate();
  const { friendId } = useParams();
  const [personas, setPersonas] = useState([]);
  const [friendInfo, setFriendInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPersonaId, setExpandedPersonaId] = useState(null);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [personaPreferences, setPersonaPreferences] = useState({});
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [suggestionForm, setSuggestionForm] = useState({
    category: 'Technology',
    tag: '',
    weight: 0,
    reasoning: ''
  });

  const categories = [
    'Technology', 'Food', 'Music', 'Sports', 'Books',
    'Movies', 'Games', 'Fashion', 'Travel', 'Pets',
    'Art', 'Cooking', 'Fitness', 'Photography'
  ];

  useEffect(() => {
    loadFriendPersonas();
  }, [friendId]);

  const loadFriendPersonas = async () => {
    try {
      setLoading(true);

      // Get friend's name from friends list
      const friendsResponse = await friendsAPI.getFriends();
      const friend = friendsResponse.data.find(f => f.id.toString() === friendId.toString());

      if (!friend) {
        alert('Friend not found in your friends list');
        navigate('/friends');
        return;
      }

      setFriendInfo({ id: friendId, name: friend.name, email: friend.email });

      // Get friend's personas (includes profile persona first)
      const response = await personasAPI.getFriendPersonas(friendId);
      setPersonas(response.data);

    } catch (error) {
      console.error('Failed to load friend personas:', error);
      if (error.response?.status === 403) {
        alert('You can only view personas of your friends');
        navigate('/friends');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPersonaPreferences = async (personaId, forceReload = false) => {
    if (personaPreferences[personaId] && !forceReload) return;

    try {
      setPrefsLoading(true);
      const response = await preferencesAPI.getForSubject('persona', personaId);
      setPersonaPreferences(prev => ({
        ...prev,
        [personaId]: response.data
      }));
    } catch (error) {
      console.error('Failed to load persona preferences:', error);
      setPersonaPreferences(prev => ({
        ...prev,
        [personaId]: []
      }));
    } finally {
      setPrefsLoading(false);
    }
  };

  const togglePersonaExpansion = async (personaId) => {
    if (expandedPersonaId === personaId) {
      setExpandedPersonaId(null);
    } else {
      setExpandedPersonaId(personaId);
      if (!personaPreferences[personaId]) {
        await loadPersonaPreferences(personaId);
      }
    }
  };

  const getWeightColor = (weight) => {
    if (weight >= 3) return 'teal';
    if (weight >= 1) return 'green';
    if (weight <= -3) return 'red';
    if (weight <= -1) return 'orange';
    return 'gray';
  };

  const getWeightText = (weight) => {
    if (weight > 0) return `+${weight}`;
    return weight === 0 ? '±0' : weight.toString();
  };

  const isPersonaFilledOut = (persona) => {
    // Handle profile personas differently - they have data directly available
    if (persona.is_profile_persona) {
      const hasBasicInfo = persona.age || persona.gender;
      const hasTraits = persona.traits && persona.traits.length > 0;
      const hasInterests = (persona.occupations && persona.occupations.length > 0) ||
                          (persona.activities && persona.activities.length > 0) ||
                          (persona.hobbies && persona.hobbies.length > 0) ||
                          (persona.styles && persona.styles.length > 0);
      // Exclude the default "not filled out" message from being considered meaningful
      const hasNotes = persona.notes &&
                      persona.notes.trim().length > 10 &&
                      !persona.notes.includes("hasn't filled out their profile yet");
      const hasPreferences = persona.preferences && persona.preferences.length > 0;

      // Need at least some combination of data for profile personas
      return hasBasicInfo || hasTraits || hasInterests || hasNotes || hasPreferences;
    }

    // For regular personas, check if they have meaningful data for gift recommendations
    const hasBasicInfo = persona.age || persona.gender;
    const hasTraits = persona.traits && persona.traits.length > 0;
    const hasInterests = (persona.occupations && persona.occupations.length > 0) ||
                        (persona.activities && persona.activities.length > 0) ||
                        (persona.hobbies && persona.hobbies.length > 0) ||
                        (persona.styles && persona.styles.length > 0);
    const hasNotes = persona.notes && persona.notes.trim().length > 10; // Meaningful notes
    const hasPreferences = persona.preferences && persona.preferences.length > 0;

    // Need at least some combination of data
    return hasBasicInfo || hasTraits || hasInterests || hasNotes || hasPreferences;
  };

  const handleFindGifts = async (persona) => {
    try {
      // Check if we already have a shopping persona for this friend/persona combination
      const friendName = friendInfo?.name || 'Friend';
      const expectedName = `Gift for ${friendName} (${persona.name})`;

      // Get all user personas to find existing shopping personas
      const userPersonasResponse = await personasAPI.getAll();
      const userPersonas = userPersonasResponse.data || [];

      // Look for existing shopping persona
      const existingShoppingPersona = userPersonas.find(p =>
        p.name === expectedName &&
        p.notes &&
        p.notes.includes(`Based on ${friendName}'s shared persona`)
      );

      let shoppingPersona;

      if (existingShoppingPersona) {
        // Reuse existing shopping persona - no need to update
        shoppingPersona = existingShoppingPersona;
        console.log('Reusing existing shopping persona:', shoppingPersona.id);
      } else {
        // Create new shopping persona with 1-hour expiration
        const oneHourFromNow = new Date();
        oneHourFromNow.setHours(oneHourFromNow.getHours() + 1);

        const shoppingPersonaData = {
          name: expectedName,
          age: persona.age,
          notes: `Based on ${friendName}'s shared persona: ${persona.notes || ''}`,
          gender: persona.gender,
          traits: persona.traits || [],
          occupations: persona.occupations || [],
          activities: persona.activities || [],
          hobbies: persona.hobbies || [],
          styles: persona.styles || [],
          relationship: persona.relationship,
          favorite_holiday: persona.favorite_holiday,
          city: persona.city,
          state: persona.state,
          email: persona.email,
          phone: persona.phone,
          is_temporary: true, // Mark as temporary shopping persona
          expires_at: oneHourFromNow.toISOString() // Expire in 1 hour
        };

        const createResponse = await personasAPI.create(shoppingPersonaData);
        shoppingPersona = createResponse.data;
        console.log('Created new shopping persona:', shoppingPersona.id);
      }

      // Navigate to products page with the shopping persona selected
      navigate(`/products?persona=${shoppingPersona.id}`);
    } catch (error) {
      console.error('Failed to prepare shopping persona:', error);
      alert('Failed to prepare gift recommendations. Please try again.');
    }
  };

  const handleCreateSuggestion = async () => {
    if (!selectedPersona || !suggestionForm.tag.trim()) return;

    try {
      await personaPrivacyAPI.createSuggestion({
        persona_id: selectedPersona.id,
        category: suggestionForm.category,
        tag: suggestionForm.tag,
        weight: suggestionForm.weight,
        reasoning: suggestionForm.reasoning
      });

      setShowSuggestionModal(false);
      setSelectedPersona(null);
      setSuggestionForm({
        category: 'Technology',
        tag: '',
        weight: 0,
        reasoning: ''
      });
      alert('Suggestion submitted successfully!');
    } catch (error) {
      alert('Failed to create suggestion: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <Center h="70vh">
        <Stack align="center" gap="md">
          <Loader size="xl" type="dots" />
          <Text c="dimmed" size="lg">Loading friend's personas...</Text>
        </Stack>
      </Center>
    );
  }

  const breadcrumbItems = [
    { title: 'Friends', href: '/friends' },
    { title: friendInfo?.name || `Friend ${friendId}`, href: '#' }
  ].map((item, index) => (
    <Anchor key={index} href={item.href} onClick={(e) => { e.preventDefault(); navigate(item.href); }}>
      {item.title}
    </Anchor>
  ));

  return (
    <Box style={{ background: 'linear-gradient(180deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)' }}>
      <Container size="xl" py="xl" px="md">
        {/* Breadcrumbs */}
        <Box mb="lg">
          <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>
        </Box>

        {/* Header */}
        <Box mb="xl" py="lg">
          <Group justify="space-between" align="flex-start">
            <Box>
              <Title
                order={1}
                size="h1"
                fw={700}
                style={{
                  background: 'linear-gradient(45deg, #1971c2 0%, #15aabf 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '8px'
                }}
              >
                👥 {friendInfo?.name || `Friend ${friendId}`}'s Personas
              </Title>
              <Text size="xl" c="dimmed" fw={400}>
                View personas shared by your friend
              </Text>
            </Box>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/friends')}
              style={{ boxShadow: '0 4px 12px rgba(25,113,194,0.1)' }}
            >
              ← Back to Friends
            </Button>
          </Group>
        </Box>

        {/* Personas Grid */}
        {personas.length === 0 ? (
          <Card
            p="xl"
            radius="lg"
            withBorder
            style={{
              background: 'linear-gradient(135deg, rgba(25,113,194,0.05) 0%, rgba(0,0,0,0) 100%)',
              borderStyle: 'dashed',
              borderWidth: '2px'
            }}
          >
            <Center py="xl">
              <Stack align="center" gap="md">
                <ThemeIcon size={100} radius="xl" variant="light" color="blue">
                  <Text size="60px">🎭</Text>
                </ThemeIcon>
                <Title order={2} ta="center">
                  No personas shared
                </Title>
                <Text c="dimmed" ta="center" maw={500}>
                  Your friend hasn't created any personas yet, or hasn't shared them with friends
                </Text>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/friends')}
                  mt="md"
                >
                  Back to Friends
                </Button>
              </Stack>
            </Center>
          </Card>
        ) : (
          <>
            {/* Profile Persona - Display prominently */}
            {personas.length > 0 && personas[0].is_profile_persona && (
              <Card
                p="xl"
                radius="lg"
                withBorder
                shadow="lg"
                mb="xl"
                style={{
                  background: 'linear-gradient(135deg, rgba(16,185,129,0.05) 0%, rgba(0,0,0,0) 100%)',
                  borderColor: 'rgba(16,185,129,0.3)',
                  borderWidth: '2px'
                }}
              >
                <Group justify="space-between" mb="lg">
                  <Box>
                    <Group gap="sm" mb="xs">
                      <Title order={2} style={{
                        background: 'linear-gradient(45deg, #10b981 0%, #059669 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>
                        👤 {personas[0].name}
                      </Title>
                      <Badge size="lg" color="teal" variant="light">
                        Profile Persona
                      </Badge>
                    </Group>
                    <Text size="lg" c="dimmed">
                      {friendInfo?.name}'s personal profile and preferences
                    </Text>
                  </Box>
                  <Button
                    variant="light"
                    color={isPersonaFilledOut(personas[0]) ? "blue" : "gray"}
                    disabled={!isPersonaFilledOut(personas[0])}
                    onClick={() => handleFindGifts(personas[0])}
                  >
                    {isPersonaFilledOut(personas[0]) ? '🎁 Find Gifts' : '📝 Persona Not Complete'}
                  </Button>
                </Group>

                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md" mb="lg">
                  {personas[0].age && (
                    <Paper p="md" withBorder radius="md">
                      <Text size="sm" c="dimmed" mb="xs">Age</Text>
                      <Text size="lg" fw={600}>{personas[0].age}</Text>
                    </Paper>
                  )}

                  {personas[0].gender && (
                    <Paper p="md" withBorder radius="md">
                      <Text size="sm" c="dimmed" mb="xs">Gender</Text>
                      <Text size="lg" fw={600}>{personas[0].gender}</Text>
                    </Paper>
                  )}

                  {personas[0].traits && personas[0].traits.length > 0 && (
                    <Paper p="md" withBorder radius="md">
                      <Text size="sm" c="dimmed" mb="xs">Personality Traits</Text>
                      <Group gap="xs" mt="xs">
                        {personas[0].traits.map((trait, idx) => (
                          <Badge key={idx} variant="light" color="grape" size="sm">
                            {trait}
                          </Badge>
                        ))}
                      </Group>
                    </Paper>
                  )}

                  {personas[0].occupations && personas[0].occupations.length > 0 && (
                    <Paper p="md" withBorder radius="md">
                      <Text size="sm" c="dimmed" mb="xs">What They Do</Text>
                      <Group gap="xs" mt="xs">
                        {personas[0].occupations.map((occ, idx) => (
                          <Badge key={idx} variant="light" color="cyan" size="sm">
                            {occ}
                          </Badge>
                        ))}
                      </Group>
                    </Paper>
                  )}

                  {personas[0].activities && personas[0].activities.length > 0 && (
                    <Paper p="md" withBorder radius="md">
                      <Text size="sm" c="dimmed" mb="xs">Activities</Text>
                      <Group gap="xs" mt="xs">
                        {personas[0].activities.map((act, idx) => (
                          <Badge key={idx} variant="light" color="green" size="sm">
                            {act}
                          </Badge>
                        ))}
                      </Group>
                    </Paper>
                  )}

                  {personas[0].hobbies && personas[0].hobbies.length > 0 && (
                    <Paper p="md" withBorder radius="md">
                      <Text size="sm" c="dimmed" mb="xs">Hobbies</Text>
                      <Group gap="xs" mt="xs">
                        {personas[0].hobbies.map((hobby, idx) => (
                          <Badge key={idx} variant="light" color="pink" size="sm">
                            {hobby}
                          </Badge>
                        ))}
                      </Group>
                    </Paper>
                  )}

                  {personas[0].styles && personas[0].styles.length > 0 && (
                    <Paper p="md" withBorder radius="md">
                      <Text size="sm" c="dimmed" mb="xs">Style Preferences</Text>
                      <Group gap="xs" mt="xs">
                        {personas[0].styles.map((style, idx) => (
                          <Badge key={idx} variant="light" color="indigo" size="sm">
                            {style}
                          </Badge>
                        ))}
                      </Group>
                    </Paper>
                  )}
                </SimpleGrid>

                {personas[0].notes && (
                  <Paper p="md" withBorder radius="md" mb="lg">
                    <Text size="sm" c="dimmed" mb="xs">About Them</Text>
                    <Text>{personas[0].notes}</Text>
                  </Paper>
                )}

                {/* Profile Preferences */}
                {personas[0].preferences && personas[0].preferences.length > 0 && (
                  <Box>
                    <Text fw={600} mb="md">Their Likes & Dislikes</Text>
                    <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="sm">
                      {personas[0].preferences.map(pref => (
                        <Paper
                          key={pref.id}
                          p="sm"
                          withBorder
                          radius="md"
                          style={{
                            transition: 'all 0.2s ease',
                            cursor: 'default'
                          }}
                        >
                          <Stack gap="xs">
                            <Badge size="xs" variant="light" color="grape">
                              {pref.category}
                            </Badge>
                            <Text size="sm" fw={500} lineClamp={2}>
                              {pref.tag}
                            </Text>
                            <Badge
                              size="sm"
                              color={getWeightColor(pref.weight)}
                              variant="filled"
                            >
                              {getWeightText(pref.weight)}
                            </Badge>
                          </Stack>
                        </Paper>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
              </Card>
            )}

            {/* Other Personas */}
            {personas.filter(p => !p.is_profile_persona).length > 0 && (
              <>
                <Title order={2} mb="lg">
                  🎭 Additional Personas
                </Title>
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                  {personas.filter(p => !p.is_profile_persona).map((persona) => (
              <Card
                key={persona.id}
                p="lg"
                radius="lg"
                withBorder
                shadow="sm"
                style={{
                  transition: 'all 0.3s ease',
                  background: 'linear-gradient(135deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                <Group justify="space-between" mb="md">
                  <Text
                    fw={700}
                    size="xl"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/products?persona=${persona.id}`)}
                  >
                    {persona.name}
                  </Text>
                  <ActionIcon
                    variant="subtle"
                    color="blue"
                    size="lg"
                    onClick={() => togglePersonaExpansion(persona.id)}
                  >
                    {expandedPersonaId === persona.id ? '−' : '+'}
                  </ActionIcon>
                </Group>

                <Stack gap="xs" mb="md">
                  {persona.age && (
                    <Group gap="xs">
                      <ThemeIcon size="sm" radius="xl" variant="light" color="blue">
                        🎂
                      </ThemeIcon>
                      <Text size="sm" c="dimmed">Age {persona.age}</Text>
                    </Group>
                  )}
                  {persona.relationship && (
                    <Group gap="xs">
                      <ThemeIcon size="sm" radius="xl" variant="light" color="pink">
                        💝
                      </ThemeIcon>
                      <Text size="sm" c="dimmed">{persona.relationship}</Text>
                    </Group>
                  )}
                  {(persona.city && persona.state) && (
                    <Group gap="xs">
                      <ThemeIcon size="sm" radius="xl" variant="light" color="teal">
                        📍
                      </ThemeIcon>
                      <Text size="sm" c="dimmed">{persona.city}, {persona.state}</Text>
                    </Group>
                  )}
                  {persona.favorite_holiday && (
                    <Group gap="xs">
                      <ThemeIcon size="sm" radius="xl" variant="light" color="orange">
                        🎉
                      </ThemeIcon>
                      <Text size="sm" c="dimmed">
                        {persona.favorite_holiday.charAt(0).toUpperCase() + persona.favorite_holiday.slice(1).replace('-', ' ')}
                      </Text>
                    </Group>
                  )}
                </Stack>

                {persona.notes && (
                  <Text size="sm" c="dimmed" mb="md" lineClamp={3}>
                    {persona.notes}
                  </Text>
                )}

                <Group grow mb="md">
                  <Button
                    variant="light"
                    color={isPersonaFilledOut(persona) ? "blue" : "gray"}
                    disabled={!isPersonaFilledOut(persona)}
                    onClick={() => handleFindGifts(persona)}
                  >
                    {isPersonaFilledOut(persona) ? '🎁 Find Gifts' : '📝 Persona Not Complete'}
                  </Button>
                </Group>

                <Collapse in={expandedPersonaId === persona.id}>
                  <Divider my="md" />
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Text fw={600} size="sm">
                        Preferences & Interests
                      </Text>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => {
                          setSelectedPersona(persona);
                          setShowSuggestionModal(true);
                        }}
                      >
                        💡 Suggest Trait
                      </Button>
                    </Group>

                    {prefsLoading ? (
                      <Center py="sm">
                        <Loader size="sm" />
                      </Center>
                    ) : personaPreferences[persona.id] && personaPreferences[persona.id].length > 0 ? (
                      <ScrollArea h={200}>
                        <Stack gap="xs">
                          {personaPreferences[persona.id].map(pref => (
                            <Paper key={pref.id} p="xs" withBorder radius="md">
                              <Group justify="space-between">
                                <Group gap="xs">
                                  <Badge size="xs" variant="light">
                                    {pref.category}
                                  </Badge>
                                  <Text size="sm">{pref.tag}</Text>
                                </Group>
                                <Badge
                                  size="sm"
                                  color={getWeightColor(pref.weight)}
                                  variant="filled"
                                >
                                  {getWeightText(pref.weight)}
                                </Badge>
                              </Group>
                            </Paper>
                          ))}
                        </Stack>
                      </ScrollArea>
                    ) : (
                      <Text size="sm" c="dimmed" ta="center" py="sm">
                        No preferences yet
                      </Text>
                    )}
                  </Stack>
                </Collapse>
              </Card>
            ))}
                </SimpleGrid>
              </>
            )}
          </>
        )}

        {/* Suggestion Modal */}
        <Modal
          opened={showSuggestionModal}
          onClose={() => setShowSuggestionModal(false)}
          title={`Suggest a trait for ${selectedPersona?.name}`}
          size="md"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Suggest a preference or interest that you think {friendInfo?.name} might have for this persona.
            </Text>

            <Group grow>
              <Select
                label="Category"
                data={categories}
                value={suggestionForm.category}
                onChange={(value) => setSuggestionForm({ ...suggestionForm, category: value })}
              />
              <TextInput
                label="Trait/Preference"
                placeholder="e.g., coffee, gaming"
                value={suggestionForm.tag}
                onChange={(e) => setSuggestionForm({ ...suggestionForm, tag: e.target.value })}
              />
            </Group>

            <Select
              label="Importance Level"
              data={Array.from({length: 11}, (_, i) => {
                const val = i - 5;
                return { value: val.toString(), label: val > 0 ? `+${val}` : val.toString() };
              })}
              value={suggestionForm.weight.toString()}
              onChange={(value) => setSuggestionForm({ ...suggestionForm, weight: parseInt(value) })}
            />

            <Textarea
              label="Why do you think this? (optional)"
              placeholder="Share your reasoning..."
              value={suggestionForm.reasoning}
              onChange={(e) => setSuggestionForm({ ...suggestionForm, reasoning: e.target.value })}
              rows={3}
            />

            <Group grow>
              <Button onClick={handleCreateSuggestion} disabled={!suggestionForm.tag.trim()}>
                Send Suggestion
              </Button>
              <Button variant="outline" onClick={() => setShowSuggestionModal(false)}>
                Cancel
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Container>
    </Box>
  );
};

export default FriendPersonasPage;
